// ─────────────────────────────────────────────────────────────────────
// IS THE PAGE STILL THE BUILD THAT IS DEPLOYED? (#537)
//
// A deploy replaces the bundle on the server; an open tab keeps running the
// one it loaded. Nothing breaks — there are no lazy chunks in this app, so
// the loaded page stays whole — but it is quietly OLD, and the person using
// it has no way to know they are looking at yesterday's product while
// somebody demonstrates today's.
//
// WHAT THIS DELIBERATELY DOES NOT DO: reload. An automatic refresh is the
// single action that destroys work in progress — it is the exact harm #537
// is about, and doing it automatically would be doing it to somebody who did
// not choose it and could not have saved first. So this OFFERS, and the
// person decides when.
//
// NO ANSWER MEANS NOTHING TO SAY. `vite serve` emits no `build.json`, so in
// development the fetch 404s and this stays quiet — a changed bundle is the
// point there, not a surprise. A network failure is likewise silence rather
// than a claim: «there might be a new version» is not worth interrupting for.
// ─────────────────────────────────────────────────────────────────────

/** Written into the bundle at build time (vite `define`). */
declare global {
  interface Window {
    __VITE_BUILD_ID__?: string;
  }
}

const listeners = new Set<(stale: boolean) => void>();
let stale = false;

/**
 * WHY the page is known to be old (#1244, RETRO-013).
 *
 *   'newer_build'   the poll saw a different `buildId` — the page still works,
 *                   it is merely yesterday's;
 *   'refused_read'  the API refused one of this bundle's reads (400
 *                   `invalid_limit` on the calendar or a club list after a
 *                   cutover like #1196/#1224) — the page is NOT working: the
 *                   calendar it shows is empty, not free.
 *
 * The second outranks the first and is never softened back: a later poll
 * saying «newer build» adds nothing to «this one cannot read».
 */
export type StaleReason = 'newer_build' | 'refused_read';
let reason: StaleReason | null = null;

function publish(next: boolean) {
  // ONE WAY ONLY. Once a newer build is deployed, it stays newer — and a
  // banner that flickered off because one poll failed would be worse than
  // one that never appeared.
  if (next === stale || !next) return;
  stale = true;
  for (const l of listeners) l(true);
}

export const newBuildAvailable = (): boolean => stale;
export const newBuildReason = (): StaleReason | null => reason;

/** The page is old, and here is how we know. Still OFFERS, never reloads. */
export function reportStaleBundle(why: StaleReason): void {
  if (reason !== 'refused_read') reason = why;
  publish(true);
}

/**
 * Was this answer the API refusing a read this bundle no longer makes
 * correctly? `400 invalid_limit` is what the paged reads answer to a bare
 * request from before the cutover — nothing else answers it. Reported, and
 * the caller still returns what a failed read always returned. Answers
 * whether it was this refusal, so the caller does not also report the read
 * as a plain failure (#1247): one cause, one banner.
 */
export async function noteRefusedRead(res: Response): Promise<boolean> {
  if (res.status !== 400) return false;
  try {
    const body = (await res.clone().json()) as { error?: unknown } | null;
    if (body?.error !== 'invalid_limit') return false;
    reportStaleBundle('refused_read');
    return true;
  } catch {
    // Not a JSON refusal, then not this one.
    return false;
  }
}

export function subscribeNewBuild(fn: (stale: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Ask the deployment what it is serving now.
 *
 * `cache: 'no-store'` and a cache-busting query, because the whole question
 * is «has this changed», and an answer served from the cache is the old one
 * by definition.
 */
export async function checkForNewBuild(
  fetchImpl: typeof fetch = (input, init) => fetch(input, init),
): Promise<void> {
  const mine = window.__VITE_BUILD_ID__;
  // An unstamped bundle cannot be compared to anything. That is every dev
  // build, and it is silence rather than a guess.
  if (!mine) return;
  try {
    const res = await fetchImpl(`/build.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return;
    const body = (await res.json()) as { buildId?: unknown };
    if (typeof body?.buildId === 'string' && body.buildId && body.buildId !== mine) {
      reportStaleBundle('newer_build');
    }
  } catch {
    // Offline, or the file is not there. Neither is news.
  }
}

/** Tests only. */
export function resetNewBuild(): void {
  stale = false;
  reason = null;
  listeners.clear();
}
